import { Typography } from "@mui/material";
import type { GridColDef, GridGroupingColDefOverride, GridKeyValue } from "@mui/x-data-grid-premium";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { parseISO } from "date-fns";
import { useCallback, useMemo } from "react";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import DataGridGroupHeader from "@entrilia/shared/components/grid/DataGridGroupHeader";
import { useGridGroupsCollapsed } from "@entrilia/shared/components/grid/gridHooks";
import type { Maybe } from "@entrilia/shared/types";
import { formatDateTime } from "@entrilia/shared/utilities/dateUtils";
import type { ClientInfo } from "../../../../api/types/clientTypes";
import type { DataModelConfiguration } from "../../../../api/types/dataModelConfigurationTypes";
import useClientContext from "../../../../context/useClientContext";
import useUserContext from "../../../../context/useUserContext";
import AssignClientMenuButton from "./AssignClientMenuButton";
import type { ConfigurationRow } from "./configurationsGridProvider";
import { ClientTitleCell, ConfigurationActionsCell, ConfigurationNameGroupingCell } from "./configurationsGridProvider";

const groupingKeySeparator = "|||";

const createGroupingKey = (config: DataModelConfiguration) => {
  const modifiedTimestamp = config.updateAt ? parseISO(config.updateAt).getTime() : 0;
  return [config.id, config.name, config.clientCodes.length, modifiedTimestamp].join(groupingKeySeparator);
};

const getRows = (
  configurations: DataModelConfiguration[],
  orgClientCode: string,
  allClients: ClientInfo[]
): ConfigurationRow[] => {
  const companies = allClients.filter((c) => c.type === "FundManager" && c.managedByClients.includes(orgClientCode));
  const companyMap = new Map(companies.map((client) => [client.clientCode, client]));
  return configurations.flatMap((config) => {
    const clients = config.clientCodes.map((clientCode) => companyMap.get(clientCode)).filter(Boolean) as ClientInfo[];
    const group = createGroupingKey(config);
    if (clients.length === 0) {
      return [
        {
          id: `${config.id}_empty`,
          group,
          configurationId: config.id,
          clientCode: "",
          clientTitle: "",
          clientLogoUrl: undefined,
          isEmpty: true,
        },
      ];
    }

    return clients.map((client) => ({
      id: `${config.id}_${client.clientCode}`,
      group,
      configurationId: config.id,
      clientCode: client.clientCode,
      clientTitle: client.title,
      clientLogoUrl: client.branding.logoMarkUrl,
    }));
  });
};

const parseGroupingKey = (groupingKey: Maybe<GridKeyValue>) => {
  const [id, name, clientCodesLength, modifiedTimestamp] = (groupingKey?.toString() ?? "").split(groupingKeySeparator);
  return {
    id,
    name,
    clientCodesLength: clientCodesLength ? parseInt(clientCodesLength, 10) : 0,
    modifiedTimestamp: modifiedTimestamp ? parseInt(modifiedTimestamp, 10) : 0,
  };
};

const groupingColumnDefinition: GridGroupingColDefOverride<ConfigurationRow> = {
  headerName: "Name",
  flex: 2,
  hideDescendantCount: true,
  renderHeader: (params) => <DataGridGroupHeader params={params} />,
  renderCell: ({ id, row, rowNode }) => {
    if (rowNode.type === "group") {
      return (
        <ConfigurationNameGroupingCell
          id={id}
          rowNode={rowNode}
          name={parseGroupingKey(rowNode.groupingKey)?.name ?? ""}
        />
      );
    }

    return <ClientTitleCell row={row} />;
  },
};

const getColumnDefinitions = ({
  hasEditPermissions,
}: {
  hasEditPermissions: boolean;
}): GridColDef<ConfigurationRow>[] => {
  const columns: GridColDef<ConfigurationRow>[] = [
    {
      field: "clientCodes",
      headerName: "Companies",
      align: "center",
      headerAlign: "center",
      flex: 1,
      renderCell: ({ rowNode }) => {
        if (rowNode.type !== "group") {
          return null;
        }

        const { clientCodesLength } = parseGroupingKey(rowNode.groupingKey);
        return <Typography>{clientCodesLength || "None"}</Typography>;
      },
    },
    {
      field: "updateAt",
      headerName: "Modified at",
      flex: 1,
      renderCell: ({ rowNode }) => {
        if (rowNode.type !== "group") {
          return null;
        }

        const { modifiedTimestamp } = parseGroupingKey(rowNode.groupingKey);
        return <Typography>{modifiedTimestamp ? formatDateTime(new Date(modifiedTimestamp)) : "-"}</Typography>;
      },
    },
  ];

  if (hasEditPermissions) {
    columns.push({
      field: "actions",
      headerName: "Actions",
      width: 80,
      cellClassName: "grid-row-actions",
      align: "right",
      headerAlign: "right",
      renderCell: ({ row, rowNode }) => {
        if (rowNode.type !== "group") {
          return row.clientCode ? (
            <AssignClientMenuButton
              clientCode={row.clientCode}
              clientTitle={row.clientTitle}
              currentConfigurationId={row.configurationId}
            />
          ) : null;
        }

        const { id } = parseGroupingKey(rowNode.groupingKey);
        return id ? <ConfigurationActionsCell configurationId={id} /> : null;
      },
    });
  }

  return columns;
};

interface Props {
  configurations: DataModelConfiguration[];
  loading: boolean;
  hasEditPermissions: boolean;
}

const ConfigurationsGrid = ({ configurations, loading, hasEditPermissions }: Props) => {
  const { clients } = useUserContext();
  const { clientCode } = useClientContext();

  const gridApiRef = useGridApiRef();
  const { isGroupExpanded } = useGridGroupsCollapsed(gridApiRef);

  const rows = useMemo(() => getRows(configurations ?? [], clientCode, clients), [clientCode, clients, configurations]);

  const columns = useMemo(() => getColumnDefinitions({ hasEditPermissions }), [hasEditPermissions]);

  const getTreeDataPath = useCallback((row: ConfigurationRow) => [row.group, row.clientCode], []);

  return (
    <DataGrid<ConfigurationRow>
      apiRef={gridApiRef}
      treeData
      getTreeDataPath={getTreeDataPath}
      groupingColDef={groupingColumnDefinition}
      columns={columns}
      rows={rows}
      loading={loading}
      isGroupExpandedByDefault={isGroupExpanded}
      noRowsText="No configurations yet"
      disableColumnReorder
      disableColumnSorting
      disableMultipleRowSelection
    />
  );
};

export default ConfigurationsGrid;
